import React, { CSSProperties, useEffect, useRef, useState } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { Col, Form, Row } from "react-bootstrap";
import { LoadingOverlay, Loader } from "react-overlay-loader";
import Select, { ActionMeta, components } from "react-select";
import _ from "lodash";

import { DateFormatConstants, NewDRConstants, TemplateValidationConstants } from "src/Core/Common/Constants";
import {
    IDocumentRequestRecipient,
    INewDocumentRequestModel,
    INewMultipleDocumentRequestModel,
    initialNewDocumentState,
    initialNewMultipleDocumentRequestModel,
} from "src/Core/ViewModels/documentRequest/DocumentRequestModel";
import Header from "src/components/Common/Navigation/Header/Header";
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    InfoCircleIcon,
} from "src/assets/images/svg/SideMenuIconCollection";
import { ColorConstants } from "src/assets/custom/colors";
import {
    IDocumentRequestTemplateFile,
    IDocumentRequestTemplateModel,
    initialTemplateTableRequestObject,
} from "src/Core/ViewModels/Template/TemplateModel";
import { AddFilesRow } from "src/components/Compose/Parts/AddFilesRow";
import { getTaxYears, isDateEqual } from "src/helper/DateHelperFunctions";
import CustomDatePicker from "src/components/Common/ui/CustomDatePicker";
import { ApplicationState } from "src/store";
import { getCompanyAuthMethods, getDefaultTaxYear, getRetentionOptions } from "src/helper/HelperFunctions";
import { AuthenticationMethod, SettingMode, DropOffLinkType, MessageSource } from "src/Core/Common/Enums";
import CustomToggleSwitch from "src/components/Common/ui/Switch/CustomToggleSwitch";
import { fetchTemplateTableData, getTemplateDetail } from "src/actionCreators/templateActionCreators";
import { fetchAddInEmailTemplate, fetchAuthRetentionPeriods } from "src/actionCreators/composeActionCreators";
import { sendNewDocumentRequest } from "src/actionCreators/documentRequestActionCreators";
import { ResourceIdLocators } from "src/helper/ResourceIdLocators";
import { AppNotifier } from "src/components/Common/Notification/AppNotifier";
import { fetchUserSettings } from "src/actionCreators/userSettingsActionCreators";
import { getSendDREmailBodyTemplate } from "src/components/Compose/Helper/HelperFunctions";
import { downloadCompanyLogo } from "src/actions/attachmentActions";
import {
    fetchMessageSecurityQuestions,
    getRecipientDefaultQuestionAndAnswer,
    getRecipientQuestionAnswer,
} from "src/actionCreators/securityQuestionCreators";
import { requestDropOffLinks } from "src/actionCreators/dropOffActionCreators";
import {
    fetchAuthenticationMethodSettings,
    fetchRequestNotificationsSettings,
    fetchOnehubLink,
    fetchRetentionSettings
} from "src/actionCreators/adminSettingsActionCreators";
import SingleRecipient, { IOption } from "src/components/Compose/Parts/SingleRecipient";
import { ISecurityQuestionAnswer } from "src/Core/Domain/Compose/IComposeModel";
import { IMessageSecurityQuestion } from "src/Core/ViewModels/Message/MessageSecurityQuestionModel";

import "./ComposeDocumentRequest.scss";
import { getDocumentCategoriesDropdown } from "src/actionCreators/documentCategoryActionCreator";
import { PlusCircleIcon } from "src/assets/images/svg/IconCollection";
import { ToasterMessages } from "src/helper/ToasterMessages";
import { fetchDefaultSavedDRMailTemplateText } from "src/actionCreators/SavedRequestActionCreator";
import LexicalEditor from "src/components/Common/ui/LexicalEditor/LexicalEditor";

interface Options {
    value: number;
    label: string;
}

export const ComposeDocumentRequest: React.FC = () => {
    const ref = useRef<HTMLFormElement>(null);
    const [validated, setValidated] = useState<boolean>(false);
    const [documentRequestData, setDocumentRequestData] = useState<INewDocumentRequestModel>(initialNewDocumentState);
    const [documentRequestRecipients, setdocumentRequestRecipients] = useState<IDocumentRequestRecipient[]>(
        initialNewMultipleDocumentRequestModel.recipients
    );
    const [currentStep, setCurrentStep] = useState(0);
    const [toRecipient, setToRecipient] = useState<IOption>();
    const { templateList, isLoading } = useSelector((state: ApplicationState) => state?.templateState);
    const { messageOptions: messageOptionsSettings, requestNotificationSettings } = useSelector(
        (appState: ApplicationState) => appState.adminSettingsState
    );
    const [isDueDateInvalid, setIsDueDateInvalid] = useState<boolean>(false);
    const [answer, setAnswer] = useState<string>("");
    const [questionTitle, setQuestionTitle] = useState<string>("");
    const [QuestionUpdated, setQuestionUpdated] = useState<boolean>(false);
    const [filesData, setFilesData] = useState<IDocumentRequestTemplateFile[]>(initialNewDocumentState.requestedFiles);
    const clearDatePicker = useRef(null);
    const composeState = useSelector((state: ApplicationState) => state.composeState);
    const retentionOptions = !composeState.authRetentions.isLoading
        ? getRetentionOptions(composeState.authRetentions.data)
        : [];
    const isAuthMethodDisabled = !messageOptionsSettings.authenticationSettings.allowEmployeesToChangeAuthMethod;
    const securityQuestionState = useSelector((state: ApplicationState) => state.securityQuestionState);
    const authenticationMethodOptions = messageOptionsSettings.authenticationSettings.authenticationMethods.map(
        value => {
            const option = getCompanyAuthMethods().filter(method => method.value === value.toString());
            return option[0];
        }
    );
    const loggedInUserProfile = useSelector((state: ApplicationState) => state.userAuth.user.profile);

    const { userId } = useSelector((state: ApplicationState) => state.userProfile);

    const defaultQuestion = securityQuestionState.securityQuestions.data.find(question => question.isDefault === true);
    const [selectedQuestiondefault, setselectedQuestiondefault] = useState(defaultQuestion?.question);
    const [selectedTemplate, setSelectedTemplate] = useState<Options[]>([]);
    const [isRetentionUpdated, setIsRetentionUpdated] = useState(false);
    const [selectedQuestionId, setSelectedQuestionId] = useState(0);
    const userSettingsStore = useSelector((state: ApplicationState) => state.userSettingsState);
    const profile = useSelector((appState: ApplicationState) => appState.userSettingsState);
    const { isComposeLoading } = useSelector((appState: ApplicationState) => appState.documentRequestState);
    const [companyLogoUrl, setCompanyLogoUrl] = React.useState("");
    const [isQuestionAnswerLoading, setIsQuestionAnswerLoading] = useState<boolean>(false);
    const { data: categories } = useSelector((state: ApplicationState) => state.documentCategoryState);
    const [mailText, setMailText] = React.useState<string>("");
    const oneHubLink = useSelector((state: ApplicationState) => state.adminSettingsState.oneHubLink);

    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(fetchRetentionSettings());
        dispatch(fetchTemplateTableData({ ...initialTemplateTableRequestObject, pageSize: 1000 })); //Hardcoded page size to be replaced with infinite scroll logic. Fetching new data after scroll. (User Virtualized list or custom observer)
        dispatch(fetchAuthRetentionPeriods(AuthenticationMethod.ACCESSCODE));
        dispatch(fetchUserSettings());
        dispatch(fetchAuthenticationMethodSettings());
        dispatch(fetchMessageSecurityQuestions(SettingMode.BOTH));
        dispatch(fetchAddInEmailTemplate(NewDRConstants.SEND_DR_EMAIL_TEMPLATE_NAME));
        dispatch(fetchRequestNotificationsSettings());
        getSubjectFromItem();
        dispatch(
            downloadCompanyLogo((url: string) => {
                setCompanyLogoUrl(url);
            })
        );
        dispatch(requestDropOffLinks());
        dispatch(getDocumentCategoriesDropdown());
        dispatch(fetchOnehubLink());
    }, []);

    useEffect(() => {
        const updatedRecipient = documentRequestRecipients.map(recipientItem => ({
            ...recipientItem,
            question: defaultQuestion?.question,
        }));

        setdocumentRequestRecipients(updatedRecipient);
        setSelectedQuestionId(defaultQuestion?.messageSecurityQuestionId);
    }, [securityQuestionState]);

    useEffect(() => {
        const selectedQuestion = securityQuestionState.securityQuestions.data.find(
            q => q.messageSecurityQuestionId === selectedQuestionId
        );

        if (documentRequestRecipients.length == 1) {
            const email = documentRequestRecipients[0].email;
            setIsQuestionAnswerLoading(true);
            dispatch(
                getRecipientQuestionAnswer(email, selectedQuestionId, (result: ISecurityQuestionAnswer) => {
                    const recipientData = documentRequestRecipients.map(recipientItem => ({
                        ...recipientItem,
                        question: selectedQuestion?.question,
                        answer: result.answer ?? "",
                        questionId: selectedQuestionId,
                    }));
                    setdocumentRequestRecipients(recipientData);
                    setIsQuestionAnswerLoading(false);
                })
            );
        } else {
            const recipientData = documentRequestRecipients.map(recipientItem => ({
                ...recipientItem,
                question: selectedQuestion?.question,
                answer: "",
                questionId: selectedQuestionId,
            }));
            setdocumentRequestRecipients(recipientData);
            setIsQuestionAnswerLoading(false);
        }
    }, [selectedQuestionId, documentRequestRecipients.length]);

    useEffect(() => {
        if (userSettingsStore.userSettings) {
            const {
                authenticationMethods,
                defaultAuthenticationMethod: adminDefaultMethod,
                allowEmployeesToChangeDefaultMethod: allowUserToChangeAuthMethod,
            } = messageOptionsSettings.authenticationSettings;
            const { defaultAuthenticationMethod: userDefaultMethod } = userSettingsStore.userSettings.authenticationMethodSettings;
            const allowUserToChangeRetention = messageOptionsSettings.retentionSettings?.isUserAllowedToChangeMessageAndDRRetentionPeriod;

            const selectedMethod = authenticationMethods.includes(userDefaultMethod) && allowUserToChangeAuthMethod
                ? userDefaultMethod
                : adminDefaultMethod;

            const expiryDays = allowUserToChangeRetention
                ? getRetentionPeriodFromUserSettings(selectedMethod)
                : getRetentionPeriodFromAdminSettings(selectedMethod);

            setDocumentRequestData({
                ...documentRequestData,
                authenticationMethod: selectedMethod,
                expiryDays,
            });
        }
    }, [userSettingsStore.userSettings?.authenticationMethodSettings, messageOptionsSettings.authenticationSettings, messageOptionsSettings.retentionSettings]);

    useEffect(() => {
        const notifyOnCompletion = !requestNotificationSettings?.isUserAllowedToChangeRequestCompletionNotification
            ? requestNotificationSettings?.isRequestCompletionNotificationEnabled
            : userSettingsStore?.userSettings?.documentRequestNotificationsSettings?.isDocumentRequestCompletionNotificationEnabled ?? false;
        setDocumentRequestData({
            ...documentRequestData,
            notifyOnCompletion: !!notifyOnCompletion
        });
    }, [requestNotificationSettings, userSettingsStore?.userSettings?.documentRequestNotificationsSettings]);

    useEffect(() => {
        if (documentRequestData.dueDate) {
            if (!checkIsDueDateValid(documentRequestData.dueDate)) {
                setIsDueDateInvalid(true);
                clearDatePicker && clearDatePicker.current;
            }
        }
    }, [documentRequestData.documentRequestTemplateId]);

    useEffect(() => {
        const defaultTaxYear = getDefaultTaxYear(userSettingsStore);

        setDocumentRequestData((previousState) =>{
            return{
            ...previousState,
            year: defaultTaxYear,
            };
        });
    }, [userSettingsStore.userSettings]);

    const dropOffLinks = useSelector((state: ApplicationState) => state.dropOffState.dropOffLinks);
    const dropOffLinkType =
        userSettingsStore &&
        userSettingsStore.userSettings &&
        userSettingsStore.userSettings.dropOffSettings?.dropOffLinkType;
    const dropOffLinkToShowInEmail =
        dropOffLinkType && dropOffLinkType === DropOffLinkType.PERSONAL
            ? dropOffLinks.personalDropOffLink
            : dropOffLinkType === DropOffLinkType.COMPANY
                ? dropOffLinks.companyDropOffLink
                : null;

    const dropOffLinkString = dropOffLinkToShowInEmail
        ? `Send file(s) back using this <a href="${dropOffLinkToShowInEmail}">link</a>`
        : "";

    const getSelectedOption = (value?: number) => {
        if (value) {
            const result = composeState.authRetentions.data.find(rp => rp.retentionId === value);

            if (result) return result.days;
            else return 0;
        }
        return 0;
    };

    const getRetentionPeriodFromUserSettings = (authenticationMethod: AuthenticationMethod) => {
        if (!profile) return 0;

        switch (authenticationMethod) {
            case AuthenticationMethod.ACCESSCODE:
                return getSelectedOption(
                    userSettingsStore?.userSettings?.retentionSettings.defaultRetentionPeriods.accessCode
                );
            case AuthenticationMethod.QUESTIONANSWER:
                return getSelectedOption(
                    userSettingsStore?.userSettings?.retentionSettings.defaultRetentionPeriods.questionAndAnswer
                );
            default:
                return 0;
        }
    };

    const getRetentionPeriodFromAdminSettings = (authenticationMethod: AuthenticationMethod) => {
        if (!profile) return 0;

        switch (authenticationMethod) {
            case AuthenticationMethod.ACCESSCODE:
                return getSelectedOption(messageOptionsSettings.retentionSettings?.defaultRetentionPeriods.accessCode);
            case AuthenticationMethod.QUESTIONANSWER:
                return getSelectedOption(messageOptionsSettings.retentionSettings?.defaultRetentionPeriods.questionAndAnswer);
            default:
                return 0;
        }
    };

    const getSubjectFromItem = () => {
        let subject = "";
        Office.context.mailbox.item.subject.getAsync(asyncResult => {
            subject = asyncResult.value;
            setDocumentRequestData(data => {
                return {
                    ...data,
                    subject: subject,
                };
            });
        });
    };
    const getCurrentStepUI = () => {
        switch (currentStep) {
            case 0:
                return getStep1UI();
            case 1:
                return getStep2UI();
            case 2:
                return getStep3UI();
        }
    };

    const Option = (props: any) => {
        return (
            <span title={props.data.label}>
                <components.Option {...props} />
            </span>
        );
    };

    const ClearIndicator = (props: any) => {
        const {
            children = (
                <div className="clear-icon">
                    <i className="fas fa-times"></i>
                </div>
            ),
            getStyles,
            innerProps: { ref, ...restInnerProps },
        } = props;
        return (
            <div {...restInnerProps} ref={ref} style={getStyles("clearIndicator", props) as CSSProperties}>
                <div style={{ padding: "0px 5px" }}>{children}</div>
            </div>
        );
    };

    const getUsersDefaultQuestion = () => {
        return (
            securityQuestionState.securityQuestions.data.find(x => x.isDefault) ||
            securityQuestionState.securityQuestions.data[0]
        );
    };

    const getReceiptDefaultQuestionAndAnswer = () => {
        const defaultQuestion: IMessageSecurityQuestion | undefined = getUsersDefaultQuestion();
        dispatch(
            getRecipientDefaultQuestionAndAnswer(
                documentRequestRecipients[0].email,
                defaultQuestion?.messageSecurityQuestionId ?? 0,
                (result: ISecurityQuestionAnswer) => {
                    const localRecipientData = documentRequestRecipients.map(recipientItem => {
                        if (recipientItem.email == documentRequestRecipients[0].email) {
                            setQuestionTitle(result.question);
                            setSelectedQuestionId(result.securityQuestionId);
                            setAnswer(result.answer);
                            return {
                                ...recipientItem,
                                questionId: result.securityQuestionId,
                                question: result.question,
                                answer: result.answer,
                            };
                        }
                        return { ...recipientItem };
                    });
                    setdocumentRequestRecipients(localRecipientData);
                }
            )
        );
    };

    const handleStep1NextBtnClick = () => {
        if (
            !!documentRequestData.message &&
            !!documentRequestRecipients &&
            !!documentRequestData.subject &&
            documentRequestRecipients.length > 0 &&
            documentRequestRecipients[0].email
        ) {
            if (selectedQuestionId == defaultQuestion?.messageSecurityQuestionId && !QuestionUpdated) {
                getReceiptDefaultQuestionAndAnswer();
            }
            setCurrentStep(1);
            setValidated(false);
        } else {
            setValidated(true);
        }
    };

    const handleStep2NextBtnClick = () => {
        if (!documentRequestData.message) {
            AppNotifier.Warning(ToasterMessages.WARNING.EMPTY_MESSAGE);
        } else if (filesData) {
            // Validate whether added file names are empty
            const isValid = filesData.every(item => {
                return item.name && item.name.length > 0;
            });
            isValid && setCurrentStep(2);
            !isValid && setValidated(true);
        } else {
            setValidated(true);
        }
    };

    const handleRecipientChange = (payload: IDocumentRequestRecipient) => {
        const updatedRecipient1 = [{ ...payload, question: selectedQuestiondefault }];
        setdocumentRequestRecipients(updatedRecipient1);
    };

    const decodeHtml = (html: string) => {
        const txt = document.createElement("textarea");
        txt.innerHTML = html;
        const value = txt.value.replace("<p>", "").replace("</p>", "");
        return value;
    };

    const handleMessageBodyChange = (message: string): void => {
        const val = decodeHtml(message);
        if (val.trim().length === 0) {
            message = val.trim();
        }
        setDocumentRequestData((data: INewDocumentRequestModel) => {
            return {
                ...data,
                message: message,
            };
        });
    };

    const handleMailTextFetchSuccess = (data: string) => {
        setMailText(data);
    };

    const getStep1UI = () => {
        return (
            <div className="step-1-ui-container">
                <Header title={getHeaderTitle()} />
                <Form.Group className="form-group">
                    <SingleRecipient
                        setToRecipient={setToRecipient}
                        value={toRecipient}
                        inValid={validated && !documentRequestRecipients[0]?.email}
                        setValue={(value: IDocumentRequestRecipient) => {
                            handleRecipientChange(value);
                        }}
                        formLabel={NewDRConstants.TO_LABEL}
                        formLabelInfoText={NewDRConstants.TO_LABEL_INFO_TEXT}
                        question={getSelectedQuestion()}
                        questionTitle={questionTitle}
                        answer={answer}
                        setQuestionUpdated={setQuestionUpdated}
                        setSelectedQuestionId={setSelectedQuestionId}
                    />
                </Form.Group>

                <Form.Group className="form-group">
                    <Form.Label>{NewDRConstants.SUBJECT_LABEL}</Form.Label>
                    <Form.Control
                        type="text"
                        id="subject"
                        name="subject"
                        placeholder={NewDRConstants.SUBJECT_LABEL}
                        value={documentRequestData.subject}
                        onChange={onFormChange}
                        maxLength={NewDRConstants.SUBJECT_MAX_LENGTH}
                        className={validated && !documentRequestData.subject ? "error" : ""}
                    />
                </Form.Group>

                <Form.Group className="input-with-editor">
                    <div className="message-label-container">
                        <Form.Label>{NewDRConstants.MESSAGE_LABEL}</Form.Label>
                        <span title={NewDRConstants.MESSAGE_LABEL_INFO_TEXT}>
                            <InfoCircleIcon width={12} height={12} color={ColorConstants.brand_light_blue} />
                        </span>
                    </div>

                    <div className={`editor-wrapper${validated && !documentRequestData.message ? " error" : ""}`}>
                        <LexicalEditor
                            messageBody={documentRequestData.message}
                            changeStateEditorBody={e => {
                                handleMessageBodyChange(e);
                            }}
                        />
                    </div>
                </Form.Group>

                <div className="dr-footer-btn-container">
                    <button
                        className="button-primary-blue-2 continue-button-dr"
                        style={{ width: "100%" }}
                        onClick={handleStep1NextBtnClick}
                    >
                        <span>
                            {NewDRConstants.NEXT}
                            <ChevronRightIcon color={ColorConstants.white} />
                        </span>
                    </button>
                </div>
            </div>
        );
    };

    const getStep2UI = () => {
        fetchDefaultSavedDRMailTemplateText(handleMailTextFetchSuccess);
        return (
            <div>
                <Header title={getHeaderTitle()} />
                <Form.Group className="select-with-clear">
                    <Form.Label>{NewDRConstants.TEMPLATE_LABEL}</Form.Label>

                    <div title={selectedTemplate[0]?.label}>
                        <Select
                            options={getTemplateOptions()}
                            onChange={handleTemplateChange}
                            value={selectedTemplate}
                            placeholder={NewDRConstants.NO_TEMPLATE_TEXT}
                            isLoading={isLoading}
                            isClearable={Boolean(documentRequestData?.documentRequestTemplateId)}
                            classNamePrefix="template-dropdown"
                            theme={theme => {
                                return { ...theme, borderRadius: 0 };
                            }}
                            components={{
                                Option,
                                IndicatorSeparator: null,
                                ClearIndicator,
                            }}
                        />
                    </div>
                </Form.Group>

                <Row className="add-files-wrapper">
                    <div tabIndex={0} className="add-files-btn" onClick={onClickAddFiles}>
                        <PlusCircleIcon color={ColorConstants.brand_primary_blue_1} />
                        <p>{NewDRConstants.ADD_FILES_TEXT}</p>
                    </div>
                    <div className="files-wrapper-div">
                        {filesData &&
                            filesData?.length > 0 &&
                            filesData.map((file, fileIndex, files) => {
                                return (
                                    <>
                                        <AddFilesRow
                                            key={fileIndex}
                                            name={file.name}
                                            documentCategory={file.documentCategory || null}
                                            description={file.description}
                                            fileIndex={fileIndex}
                                            filesLength={files.length}
                                            descriptionRequired={false}
                                            hideErrorText={true}
                                            validated={validated}
                                            onFilesFormChange={handleFilesFormChange}
                                            onClickRemoveRow={onClickRemoveRow}
                                            onCategoryChange={handleCategoryChange}
                                        />
                                        <hr />
                                    </>
                                );
                            })}
                    </div>
                </Row>

                <div className="dr-footer-btn-container">
                    <button
                        className="button-primary-blue-2-outlined back-button-dr"
                        onClick={() => {
                            setCurrentStep(0);
                        }}
                    >
                        <span>
                            <ChevronLeftIcon color={ColorConstants.brand_light_blue} />
                            {NewDRConstants.BACK}
                        </span>
                    </button>
                    <button className="button-primary-blue-2 next-button-dr" onClick={handleStep2NextBtnClick}>
                        <span>
                            {NewDRConstants.NEXT}
                            <ChevronRightIcon color={ColorConstants.white} />
                        </span>
                    </button>
                </div>
            </div>
        );
    };

    const onClearDueDate = () => {
        clearDatePicker && clearDatePicker.current;
        setDocumentRequestData({ ...documentRequestData, dueDate: null });
        setIsDueDateInvalid(false);
    };

    const getSelectedRetention = (): number => {
        let selectedRetention: number | undefined = documentRequestData.expiryDays;
        if (!selectedRetention) {
            selectedRetention = composeState.authRetentions.data.find(x => x.isDefault)?.days;
        }

        return selectedRetention ?? 0;
    };

    const onFormChange: React.ChangeEventHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        // eslint-disable-next-line prefer-const
        let { id, value } = e.target;
        if (value.trim().length === 0) value = value.trim();
        setDocumentRequestData({ ...documentRequestData, [id]: value });
        let question: IMessageSecurityQuestion = defaultQuestion;

        switch (id) {
            case NewDRConstants.SUBJECT:
                Office.context.mailbox.item.subject.setAsync(value);
                break;
            case NewDRConstants.AUTHENTICATION_METHOD:
                if (selectedQuestionId > 0) {
                    question = getSelectedQuestion();
                }
                setSelectedQuestionId(question?.messageSecurityQuestionId);
                setDocumentRequestData(data => {
                    return {
                        ...data,
                        expiryDays: !isRetentionUpdated ? getRetentionPeriodFromUserSettings(+value) : data.expiryDays,
                        recipient: {
                            ...data.recipient,
                            question: question?.question,
                        },
                    };
                });
                break;
            case NewDRConstants.EXPIRY_DAYS:
                setIsRetentionUpdated(true);
                break;
            default:
                break;
        }
    };

    const handleError = () => {
        AppNotifier.Error(ToasterMessages.ERROR.SEND_REQUEST_FAILED);
    };

    const handleSendRequest = () => {
        // Handle Send Request
        const recipientData = documentRequestRecipients.map(recipientItem => {
            return {
                ...recipientItem,
                question: questionTitle?.trim(),
                answer: answer?.trim(),
                questionId: selectedQuestionId,
            };
        });
        const completeFormData = {
            ...documentRequestData,
            senderId: userId,
            messageSource: MessageSource.WEBADDIN,
            requestedFiles: filesData.map(file => {
                return { ...file, name: file.name?.trim(), description: file.description?.trim() };
            }),
        };
        const completeData: INewMultipleDocumentRequestModel = {
            documentRequest: completeFormData,
            recipients: recipientData,
        };

        const isQnAValid =
            Number(documentRequestData.authenticationMethod) === AuthenticationMethod.QUESTIONANSWER
                ? questionTitle && answer
                : true;
        // Validate for Invalid date and empty QnA
        if (!isDueDateInvalid && isQnAValid) {
            setValidated(false);
            dispatch(
                sendNewDocumentRequest(
                    completeData,
                    ResourceIdLocators.DRL.DRLBtnNewRequest,
                    handleSuccess,
                    handleError
                )
            );
        } else {
            setValidated(true);
        }
    };

    const handleSuccess = (link: string) => {
        if (link) {
            createEmailTemplateBody(link);
        }
    };

    const createEmailTemplateBody = link => {
        const newCompanyLogoUrl = `<img src=${companyLogoUrl}  alt="company-logo" />`;
        const template = getSendDREmailBodyTemplate(
            loggedInUserProfile.company_name,
            loggedInUserProfile.given_name,
            loggedInUserProfile.family_name,
            loggedInUserProfile.email,
            documentRequestData.dueDate ? getDisplayDueDate(NewDRConstants.DR_EMAIL_DUE_DATE_FORMAT) + "." : "",
            documentRequestData.dueDate ? "Kindly submit the required document(s) by " : "",
            newCompanyLogoUrl,
            link,
            composeState.addInEmailTemplate.data,
            dropOffLinkString,
            oneHubLink,
            mailText
        );
        Office.context.mailbox.item.body.prependAsync(
            `${template} <br /><br />`,
            { coercionType: Office.CoercionType.Html },
            asyncResult => {
                Office.context.ui.closeContainer();
                setDocumentRequestData(initialNewDocumentState);
            }
        );
    };

    const getSelectedQuestion = () => {
        return (
            selectedQuestionId &&
            securityQuestionState.securityQuestions.data.find(q => q.messageSecurityQuestionId === selectedQuestionId)
        );
    };

    const handleQuestionSelect: React.ChangeEventHandler<HTMLSelectElement> = e => {
        if (documentRequestRecipients) {
            const selectedQuestion = securityQuestionState.securityQuestions.data.find(
                q => q.messageSecurityQuestionId === Number(e.target.value)
            );
            const recipientData = documentRequestRecipients.map(recipientItem => ({
                ...recipientItem,
                question: selectedQuestion?.question,
                answer: "",
                questionId: selectedQuestionId,
            }));
            setdocumentRequestRecipients(recipientData);
            setSelectedQuestionId(selectedQuestion.messageSecurityQuestionId);
            setQuestionTitle(selectedQuestion.question);
            setAnswer("");
        }
    };

    const handleQAChange: React.ChangeEventHandler<HTMLInputElement> = e => {
        if (documentRequestData.recipient) {
            let value = e.target.value;
            const targetId = e.target.id;
            if (value.trim().length === 0) value = value.trim();
            const updatedValue = targetId === "question" ? { question: value } : { answer: value };
            const recipientData = documentRequestRecipients.map(recipientItem => ({
                ...recipientItem,
                ...updatedValue,
            }));
            setdocumentRequestRecipients(recipientData);
            setQuestionUpdated(true);
            if (targetId === "answer") {
                setAnswer(value);
            } else {
                setQuestionTitle(value);
            }
        }
    };

    const getDisplayDueDate = (format: string): string => {
        let displayDate = moment(documentRequestData.dueDate).utc().format(format);
        if (!(documentRequestData.dueDate && documentRequestData.dueDate instanceof Date)) {
            displayDate = moment(documentRequestData.dueDate.toString()).format(format);
        }
        return displayDate;
    };

    const getStep3UI = () => {
        return (
            <div className="step-3-ui-container">
                <Header title={getHeaderTitle()} />
                <h2 className="section-title">{NewDRConstants.MORE_INFORMATION}</h2>

                <Row className="year-date-container">
                    <Form.Group as={Col} xs={5} controlId="year">
                        <Form.Label>{NewDRConstants.YEAR_LABEL}</Form.Label>
                        <Form.Select
                            onChange={onFormChange}
                            id="year"
                            value={documentRequestData.year}
                            placeholder={NewDRConstants.YEAR_PLACEHOLDER_TEXT}
                            required
                        >
                            {getTaxYears().map((year, index) => (
                                <option key={index}>{year}</option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            {TemplateValidationConstants.YEAR_REQUIRED_TEXT}
                        </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group as={Col} xs={5} controlId="dueDate">
                        <Form.Label>{NewDRConstants.DUE_DATE_LABEL}</Form.Label>

                        <CustomDatePicker
                            value={
                                documentRequestData?.dueDate
                                    ? getDisplayDueDate(DateFormatConstants.DEFAULT_DATE_FORMAT)
                                    : undefined
                            }
                            minDate={new Date()}
                            className={isDueDateInvalid ? "warning-highlight" : ""}
                            clearDatePicker={clearDatePicker}
                            onChange={handleDueDateChange}
                            maxDate={moment().add(NewDRConstants.MAX_DUE_DATE, "year").toDate()}
                            resetDateValues={!documentRequestData.dueDate}
                            clearFilter={onClearDueDate}
                            onSubmit={() => {
                                //
                            }}
                        />
                    </Form.Group>
                </Row>
                <Row>
                    <Form.Group className="retention-period-group" as={Col} xs={5} controlId="expiryDays">
                        <Form.Label>{NewDRConstants.RETENTION_PERIOD_LABEL}</Form.Label>
                        <Form.Select value={getSelectedRetention()} onChange={onFormChange} disabled={!messageOptionsSettings?.retentionSettings.isUserAllowedToChangeMessageAndDRRetentionPeriod}>
                            {retentionOptions.map((option, index) => (
                                <option key={index} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Row>

                <hr />
                <div>
                    <h2 className="section-title">{NewDRConstants.AUTHENTICATION_METHOD_LABEL}</h2>
                    <Form.Group className="auth-method-select" controlId="authenticationMethod">
                        <Form.Select
                            value={documentRequestData.authenticationMethod}
                            disabled={isAuthMethodDisabled}
                            onChange={onFormChange}
                        >
                            {authenticationMethodOptions.map((option, index) => (
                                <option key={index} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    {Number(documentRequestData.authenticationMethod) === AuthenticationMethod.QUESTIONANSWER && (
                        <LoadingOverlay style={{ height: "100%" }}>
                            <Row className="auth-method-container">
                                <Form.Group controlId="questionTitle">
                                    <Form.Select onChange={handleQuestionSelect} value={selectedQuestionId}>
                                        {securityQuestionState.securityQuestions.data.map((option, index) => (
                                            <option
                                                title={option.questionTitle}
                                                key={index}
                                                value={option.messageSecurityQuestionId}
                                            >
                                                {option.questionTitle}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>

                                <Form.Group controlId="question">
                                    <Form.Control
                                        type="text"
                                        className={validated && !questionTitle ? "error" : ""}
                                        value={questionTitle}
                                        onChange={handleQAChange}
                                        title={questionTitle}
                                    />
                                </Form.Group>

                                <Form.Group controlId="answer">
                                    <Form.Control
                                        type="text"
                                        name="answer"
                                        className={validated && !answer ? "error" : ""}
                                        value={answer || ""}
                                        onChange={handleQAChange}
                                    />
                                </Form.Group>
                            </Row>
                            <Loader classNamePrefix="sm" text="" loading={isQuestionAnswerLoading} />
                        </LoadingOverlay>
                    )}
                    <div className="notify-container">
                        <CustomToggleSwitch
                            label={NewDRConstants.NOTIFY_ON_COMPLETION}
                            handleChange={() => {
                                setDocumentRequestData(data => {
                                    return {
                                        ...data,
                                        notifyOnCompletion: !data.notifyOnCompletion,
                                    };
                                });
                            }}
                            switched={
                                !requestNotificationSettings?.isUserAllowedToChangeRequestCompletionNotification
                                    ? requestNotificationSettings?.isRequestCompletionNotificationEnabled
                                    : documentRequestData.notifyOnCompletion
                            }
                            disabled={!requestNotificationSettings?.isUserAllowedToChangeRequestCompletionNotification}
                        />
                    </div>
                </div>
                <div className="dr-footer-btn-container">
                    <button
                        className="button-primary-blue-2-outlined back-button-dr"
                        onClick={() => {
                            setCurrentStep(1);
                        }}
                    >
                        <span>
                            <ChevronLeftIcon color={ColorConstants.brand_light_blue} />
                            {NewDRConstants.BACK}
                        </span>
                    </button>
                    <button className="button-primary-green send-request-button-dr" onClick={handleSendRequest}>
                        <span>
                            {NewDRConstants.SEND_REQUEST}
                            <ChevronRightIcon color={ColorConstants.white} />
                        </span>
                    </button>
                </div>
            </div>
        );
    };

    const checkIsDueDateValid = (date: Date | undefined) => {
        date = moment(date).toDate();
        const maxDate = moment().add(NewDRConstants.MAX_DUE_DATE, "year").toDate();
        const currentDate = new Date();
        return (date < maxDate && date > currentDate) || isDateEqual(date, currentDate);
    };
    const handleDueDateChange = (date: Date | undefined) => {
        date = moment(date).toDate();

        if (!checkIsDueDateValid(date)) {
            setIsDueDateInvalid(true);
            clearDatePicker && clearDatePicker.current;
        } else {
            setIsDueDateInvalid(false);
            const data = { ...documentRequestData, dueDate: date };
            setDocumentRequestData(data);
        }
    };

    const handleFilesFormChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        // eslint-disable-next-line prefer-const
        let { id, value } = e.target;
        if (value.trim().length === 0) value = value.trim();

        const filesDataCopy: IDocumentRequestTemplateFile[] = _.cloneDeep(filesData);
        filesDataCopy[index][id as keyof IDocumentRequestTemplateFile] = value as any;
        setFilesData(filesDataCopy);
        setValidated(false);
    };

    const handleCategoryChange = (value: any, index: number) => {
        const filesDataCopy: IDocumentRequestTemplateFile[] = _.cloneDeep(filesData);
        filesDataCopy[index].documentCategory = value as any;
        setFilesData(filesDataCopy);
    };

    const getHeaderTitle = () => {
        switch (currentStep) {
            case 0:
                return "1. Enter Recipient Information";
            case 1:
                return "2. Create Document List";
            case 2:
                return "3. Request Configuration";
        }
    };

    const onClickRemoveRow = (index: number) => {
        const filesDataCopy = [...filesData];
        filesDataCopy.splice(index, 1);
        setFilesData(filesDataCopy);
    };
    const onClickAddFiles = () => {
        const filesDataCopy = [...filesData];
        filesDataCopy.push({
            name: "",
            description: "",
        });
        setFilesData(filesDataCopy);
    };
    const getTemplateOptions = () => {
        const options: Options[] = [];
        if (templateList && templateList.length > 0) {
            templateList.map(item => options.push({ value: item.documentRequestTemplateId, label: item.name }));
        }
        return options;
    };

    const handleTemplateChange = (value: Options | null, data: ActionMeta<Options>) => {
        switch (data.action) {
            case "select-option":
                if (value) dispatch(getTemplateDetail(value?.value, templateDetailCallback));
                break;
            case "clear":
                onClearTemplate();
                break;
        }
        setSelectedTemplate([value]);
    };

    const onClearTemplate = () => {
        const { year, dueDate, message } = initialNewDocumentState;
        setDocumentRequestData({
            ...documentRequestData,
            documentRequestTemplateId: 0,
            year,
            dueDate,
            message,
        });
        setFilesData(initialNewDocumentState.requestedFiles);
        clearDatePicker && clearDatePicker.current;
        setIsDueDateInvalid(false);
    };

    const templateDetailCallback = (data: IDocumentRequestTemplateModel) => {
        if (data) {
            getDocumentCategories(data);
            setDocumentRequestData({
                ...documentRequestData,
                documentRequestTemplateId: data.documentRequestTemplate.documentRequestTemplateId,
                message: data.documentRequestTemplate.description,
                year: data.documentRequestTemplate.year,
                dueDate: data.documentRequestTemplate.dueDate ?? null,
            });
        }
    };

    const getDocumentCategories = (data: IDocumentRequestTemplateModel) => {
        const updatedFilesData = data.files.map(file => {
            const matchingCategory = categories.find(
                category => category.documentCategoryId === file.documentCategory?.documentCategoryId
            );
            if (matchingCategory) {
                return {
                    ...file,
                    documentCategory: {
                        documentCategoryId: matchingCategory.documentCategoryId,
                        name: matchingCategory.name,
                    },
                };
            } else {
                return { ...file, documentCategory: undefined };
            }
        });
        setFilesData(updatedFilesData);
    };

    return (
        <div className="compose-dr-request-container">
            <LoadingOverlay>
                <Form
                    ref={ref}
                    onSubmit={e => {
                        e.preventDefault();
                    }}
                >
                    {getCurrentStepUI()}
                </Form>
                <Loader loading={isComposeLoading} />
            </LoadingOverlay>
        </div>
    );
};
